import { useState } from "react";
import { cn } from "@/utils/classes";

/* Prop Types */
interface RendererImageProps {
  src: string;
  alt: string;
  className?: string;
  fit?: "cover" | "contain";
}

/**
 * RendererImage component
 *
 * An <img> for renderer-backed thumbnails/frames, which can be slow to generate even at small
 * sizes. Keeps a skeleton in its place until the image has actually finished loading, instead of
 * leaving a blank gap between the surrounding skeleton disappearing and the image painting. Crops
 * to fill its box by default; pass fit="contain" to show the whole image instead.
 */
export const RendererImage = ({ src, alt, className, fit = "cover" }: RendererImageProps) => {
  /* State */
  const [isLoaded, setIsLoaded] = useState(false);

  /* Render */
  return (
    <div className={cn("relative overflow-hidden", className)}>
      {!isLoaded && <div className="skeleton absolute inset-0" />}
      <img
        src={src}
        alt={alt}
        onLoad={() => setIsLoaded(true)}
        className={cn("absolute inset-0 size-full transition-opacity duration-200", fit === "contain" ? "object-contain" : "object-cover", isLoaded ? "opacity-100" : "opacity-0")}
      />
    </div>
  );
};
